import { useMemo, useState } from 'react'
import { createFileRoute, Link } from '@tanstack/react-router'
import { useInfiniteQuery, useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { z } from 'zod'
import { flattenPages } from '@/api/pagination'
import type { ModerationActionDto, RestrictionDto } from '@/api/types'
import {
  moderationActionsQuery,
  restrictionsQuery,
  useLiftRestriction,
  type ModerationFilters,
} from '@/features/platform/platform.queries'
import {
  moderationSummaryQuery,
  rangeToBounds,
} from '@/features/analytics/analytics.queries'
import { formatDateTime, formatNumber, formatRelative } from '@/lib/format'
import {
  Badge,
  Button,
  Card,
  CardHeader,
  ConfirmDialog,
  DataTable,
  PageHeader,
  TabPanel,
  Tabs,
  useToast,
  type Column,
} from '@/ui'

const TABS = [
  { id: 'actions', labelKey: 'moderation.tabActions' },
  { id: 'restrictions', labelKey: 'moderation.tabRestrictions' },
] as const

type TabId = (typeof TABS)[number]['id']
const TAB_IDS = TABS.map((tab) => tab.id) as unknown as [TabId, ...TabId[]]

const searchSchema = z.object({
  tab: z.enum(TAB_IDS).default('actions'),
  worldSlug: z.string().optional(),
  type: z.string().optional(),
  from: z.string().optional(),
  to: z.string().optional(),
})

export const Route = createFileRoute('/_authed/moderation/')({
  validateSearch: searchSchema,
  component: ModerationPage,
})

/** Thirty days of history is what the summary above the table covers. */
const SUMMARY_DAYS = 30

function ModerationPage() {
  const { t, i18n } = useTranslation()
  const search = Route.useSearch()
  const navigate = Route.useNavigate()
  const toast = useToast()

  const filters: ModerationFilters = {
    worldSlug: search.worldSlug,
    type: search.type,
    from: search.from,
    to: search.to,
  }

  const actions = useInfiniteQuery({
    ...moderationActionsQuery(filters),
    enabled: search.tab === 'actions',
  })
  const restrictions = useInfiniteQuery({
    ...restrictionsQuery(),
    enabled: search.tab === 'restrictions',
  })

  const lift = useLiftRestriction()
  const [lifting, setLifting] = useState<RestrictionDto | null>(null)

  // Memoized as well as rounded: the bounds feed a query key, and an unstable key is a
  // render loop rather than a slow page.
  const summaryBounds = useMemo(() => rangeToBounds(SUMMARY_DAYS), [])
  const summary = useQuery(moderationSummaryQuery(summaryBounds.from, summaryBounds.to))

  const actionRows = flattenPages<ModerationActionDto>(actions.data)
  const restrictionRows = flattenPages<RestrictionDto>(restrictions.data)

  const actionColumns: Column<ModerationActionDto>[] = [
    {
      id: 'when',
      header: t('moderation.columnWhen'),
      width: '11rem',
      cell: (action) => (
        <span className="text-text-secondary">
          {formatDateTime(action.createdAt, i18n.language)}
        </span>
      ),
    },
    {
      id: 'action',
      header: t('moderation.columnAction'),
      cell: (action) => (
        <Badge tone="warning">
          {t(`moderation.type${action.action}`, { defaultValue: action.action })}
        </Badge>
      ),
    },
    {
      id: 'person',
      header: t('moderation.columnPerson'),
      cell: (action) =>
        // A room-wide action names nobody, so there is no one to link to.
        action.targetId ? (
          <Link
            to="/users/$userId"
            params={{ userId: action.targetId }}
            search={{ tab: 'overview' }}
            className="truncate font-medium text-text hover:underline"
          >
            {action.targetDisplayName ?? action.targetId}
          </Link>
        ) : (
          <span className="text-text-muted">{t('moderation.everybody')}</span>
        ),
    },
    {
      id: 'world',
      header: t('moderation.columnWorld'),
      isSecondary: true,
      cell: (action) => {
        const slug = action.worldSlug

        return slug ? (
          <Link
            to="/worlds/$slug"
            params={{ slug }}
            search={{ tab: 'overview' }}
            className="truncate text-text-secondary hover:text-text hover:underline"
          >
            {action.worldTitle ?? slug}
          </Link>
        ) : (
          <span className="text-text-muted">{t('common.notSet')}</span>
        )
      },
    },
    {
      id: 'moderator',
      header: t('moderation.columnModerator'),
      isSecondary: true,
      cell: (action) => (
        <span className="truncate text-text-secondary">{action.actorDisplayName}</span>
      ),
    },
    {
      id: 'reason',
      header: t('moderation.columnReason'),
      isSecondary: true,
      cell: (action) => (
        <span className="truncate text-text-secondary">{action.reason ?? '—'}</span>
      ),
    },
  ]

  const restrictionColumns: Column<RestrictionDto>[] = [
    {
      id: 'kind',
      header: t('moderation.columnAction'),
      cell: (restriction) => (
        // The server supplies a human name for the restriction; no need to map the key.
        <Badge tone="danger" hasDot>
          {restriction.restrictionName}
        </Badge>
      ),
    },
    {
      id: 'person',
      header: t('moderation.columnPerson'),
      cell: (restriction) => (
        <Link
          to="/users/$userId"
          params={{ userId: restriction.userId }}
          search={{ tab: 'overview' }}
          className="truncate font-medium text-text hover:underline"
        >
          {restriction.displayName}
        </Link>
      ),
    },
    {
      id: 'world',
      header: t('moderation.columnWorld'),
      cell: (restriction) => {
        const slug = restriction.worldSlug

        // A restriction can be platform-wide, in which case it belongs to no world.
        return slug ? (
          <Link
            to="/worlds/$slug"
            params={{ slug }}
            search={{ tab: 'overview' }}
            className="truncate text-text-secondary hover:text-text hover:underline"
          >
            {restriction.worldTitle ?? slug}
          </Link>
        ) : (
          <span className="text-text-muted">{t('moderation.platformWide')}</span>
        )
      },
    },
    {
      id: 'expires',
      header: t('moderation.columnExpires'),
      cell: (restriction) =>
        restriction.expiresAt ? (
          <span className="text-text-secondary">
            {formatRelative(restriction.expiresAt, i18n.language)}
          </span>
        ) : (
          <Badge tone="warning">{t('moderation.permanent')}</Badge>
        ),
    },
    {
      id: 'actions',
      header: <span className="sr-only">{t('common.actions')}</span>,
      align: 'end',
      cell: (restriction) => (
        <Button
          variant="ghost"
          size="sm"
          onClick={() => {
            setLifting(restriction)
          }}
        >
          {t('moderation.lift')}
        </Button>
      ),
    },
  ]

  return (
    <>
      <PageHeader title={t('moderation.title')} description={t('moderation.subtitle')} />

      {summary.data && summary.data.total > 0 ? (
        <Card className="mb-4">
          <CardHeader
            title={t('moderation.tabActions')}
            description={t('overview.moderationToday')}
            as="h2"
          />
          <ul className="mt-3 flex flex-wrap gap-2">
            {[...summary.data.byType]
              .sort((a, b) => b.count - a.count)
              .map((entry) => (
                <li
                  key={entry.type}
                  className="flex items-baseline gap-1.5 rounded-full border border-border bg-surface-sunken px-2.5 py-1"
                >
                  <span className="text-xs text-text-secondary">
                    {t(`moderation.type${entry.type}`, { defaultValue: entry.type })}
                  </span>
                  <span data-numeric className="text-xs font-medium text-text">
                    {formatNumber(entry.count, i18n.language)}
                  </span>
                </li>
              ))}
          </ul>
        </Card>
      ) : null}

      <Tabs
        tabs={TABS.map((tab) => ({ id: tab.id, label: t(tab.labelKey) }))}
        activeId={search.tab}
        onChange={(tab) => {
          void navigate({ search: (prev) => ({ ...prev, tab: tab as TabId }) })
        }}
        className="mb-4"
      />

      <TabPanel id={search.tab}>
        <Card isFlush>
          {search.tab === 'actions' ? (
            <DataTable
              columns={actionColumns}
              rows={actionRows.items}
              getRowId={(action) => action.id}
              caption={t('moderation.tabActions')}
              isLoading={actions.isPending}
              error={actions.error}
              onRetry={() => {
                void actions.refetch()
              }}
              emptyTitle={t('moderation.empty')}
              hasNextPage={actions.hasNextPage}
              isFetchingNextPage={actions.isFetchingNextPage}
              onLoadMore={() => {
                void actions.fetchNextPage()
              }}
            />
          ) : (
            <DataTable
              columns={restrictionColumns}
              rows={restrictionRows.items}
              getRowId={(restriction) => restriction.grantId}
              caption={t('moderation.tabRestrictions')}
              isLoading={restrictions.isPending}
              error={restrictions.error}
              onRetry={() => {
                void restrictions.refetch()
              }}
              emptyTitle={t('moderation.emptyRestrictions')}
              hasNextPage={restrictions.hasNextPage}
              isFetchingNextPage={restrictions.isFetchingNextPage}
              onLoadMore={() => {
                void restrictions.fetchNextPage()
              }}
            />
          )}
        </Card>
      </TabPanel>

      <ConfirmDialog
        isOpen={lifting !== null}
        onClose={() => {
          setLifting(null)
        }}
        onConfirm={() => {
          if (!lifting) return
          lift.mutate(
            { grantId: lifting.grantId },
            {
              onSuccess: () => {
                toast.success(t('moderation.lifted'))
                setLifting(null)
              },
              onError: (error) => {
                toast.fromError(error, t('state.errorTitle'))
              },
            },
          )
        }}
        title={t('moderation.liftConfirm')}
        body={t('moderation.liftBody', {
          name: lifting?.displayName ?? '',
          restriction: lifting?.restrictionName.toLowerCase() ?? '',
          world: lifting?.worldTitle ?? t('moderation.platformWide'),
        })}
        confirmLabel={t('moderation.lift')}
        cancelLabel={t('common.cancel')}
        isDestructive={false}
        isPending={lift.isPending}
      />
    </>
  )
}
